/**
 * Public essay detail page showing a single published essay.
 * 
 * Features:
 * - Full essay content with markdown rendering
 * - Table of contents sidebar
 * - Author information
 * - Category and tags
 * - Series navigation
 * - Public comments with login to comment
 * - Reading progress indicator
 * 
 * Called by:
 * - App.tsx as /essays/:slug route
 */
import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Typography, Spin, Alert, Tag, Space, Avatar, Button, Input, Card, Divider, message } from 'antd';
import { ClockCircleOutlined, UserOutlined, SendOutlined, ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { getPublicEssay, getPublicComments, postPublicComment, getLoginUrl, type PublicComment } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import AudioPlayer from '../../components/AudioPlayer';
import YouTubePlayer from '../../components/YouTubePlayer';
import type { AudioSegment } from '@compendium/shared';
import { format } from 'date-fns';
import { marked } from 'marked';
import hljs from 'highlight.js';
import 'highlight.js/styles/github-dark.css';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// Configure marked with syntax highlighting via renderer
const renderer = new marked.Renderer();
// Override code block rendering for syntax highlighting
renderer.code = function({ text, lang }: { text: string; lang?: string; escaped?: boolean }) {
  let highlighted: string;
  if (lang && hljs.getLanguage(lang)) {
    try {
      highlighted = hljs.highlight(text, { language: lang }).value;
    } catch {
      highlighted = text;
    }
  } else {
    highlighted = hljs.highlightAuto(text).value;
  }
  return `<pre><code class="hljs language-${lang || ''}">${highlighted}</code></pre>`;
};
marked.setOptions({ renderer });

/**
 * Comment component with nested replies.
 */
function Comment({
  comment,
  essaySlug,
  depth = 0,
}: {
  comment: PublicComment;
  essaySlug: string;
  depth?: number;
}) {
  const [showReply, setShowReply] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const replyMutation = useMutation({
    mutationFn: (content: string) => postPublicComment(essaySlug, content, comment.id),
    onSuccess: () => {
      setReplyContent('');
      setShowReply(false);
      queryClient.invalidateQueries({ queryKey: ['public', 'comments', essaySlug] });
      message.success('Reply posted!');
    },
    onError: () => {
      message.error('Failed to post reply');
    },
  });

  function handleReply() {
    if (!replyContent.trim()) return;
    replyMutation.mutate(replyContent.trim());
  }

  return (
    <div style={{ marginLeft: depth > 0 ? '32px' : 0, marginBottom: '16px' }}>
      <Card
        size="small"
        style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '8px',
        }}
        bodyStyle={{ padding: '16px' }}
      >
        <div style={{ display: 'flex', gap: '12px' }}>
          <Avatar
            src={comment.user.avatarUrl}
            icon={!comment.user.avatarUrl && <UserOutlined />}
            size="small"
          />
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: '8px' }}>
              <Text strong style={{ color: '#f5f5f5' }}>
                {comment.user.name}
              </Text>
              <Text type="secondary" style={{ marginLeft: '8px', fontSize: '0.8rem' }}>
                {format(new Date(comment.createdAt), 'MMM d, yyyy')}
              </Text>
            </div>
            <Paragraph style={{ color: '#c0c0c0', marginBottom: '8px' }}>
              {comment.content}
            </Paragraph>
            {isAuthenticated && depth < 3 && (
              <Button
                type="link"
                size="small"
                onClick={() => setShowReply(!showReply)}
                style={{ padding: 0, color: '#d4af37' }}
              >
                Reply
              </Button>
            )}
          </div>
        </div>

        {/* Reply form */}
        {showReply && (
          <div style={{ marginTop: '12px', marginLeft: '36px' }}>
            <TextArea
              placeholder="Write a reply..."
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              rows={2}
              style={{
                background: 'rgba(0, 0, 0, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            />
            <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
              <Button
                size="small"
                type="primary"
                onClick={handleReply}
                loading={replyMutation.isPending}
                style={{ background: '#d4af37', border: 'none' }}
              >
                Reply
              </Button>
              <Button size="small" onClick={() => setShowReply(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Nested replies */}
      {comment.replies?.map((reply) => (
        <Comment key={reply.id} comment={reply} essaySlug={essaySlug} depth={depth + 1} />
      ))}
    </div>
  );
}

/**
 * Comments section component.
 */
function CommentsSection({ essaySlug }: { essaySlug: string }) {
  const [newComment, setNewComment] = useState('');
  const { isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();

  const { data: comments, isLoading } = useQuery({
    queryKey: ['public', 'comments', essaySlug],
    queryFn: () => getPublicComments(essaySlug),
  });

  const postMutation = useMutation({
    mutationFn: (content: string) => postPublicComment(essaySlug, content),
    onSuccess: () => {
      setNewComment('');
      queryClient.invalidateQueries({ queryKey: ['public', 'comments', essaySlug] });
      message.success('Comment posted!');
    },
    onError: () => {
      message.error('Failed to post comment');
    },
  });

  function handlePost() {
    if (!newComment.trim()) return;
    postMutation.mutate(newComment.trim());
  }

  function handleLogin() {
    const returnTo = encodeURIComponent(window.location.pathname);
    window.location.href = `${getLoginUrl()}?returnTo=${returnTo}`;
  }

  return (
    <section style={{ marginTop: '48px' }}>
      <Title
        level={3}
        style={{
          color: '#f5f5f5',
          fontFamily: "var(--font-serif)",
          marginBottom: '24px',
        }}
      >
        Comments
      </Title>

      {/* Comment form */}
      {isAuthenticated ? (
        <Card
          style={{
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '8px',
            marginBottom: '24px',
          }}
          bodyStyle={{ padding: '16px' }}
        >
          <div style={{ display: 'flex', gap: '12px' }}>
            <Avatar
              src={user?.avatarUrl}
              icon={!user?.avatarUrl && <UserOutlined />}
            />
            <div style={{ flex: 1 }}>
              <TextArea
                placeholder="Share your thoughts..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                rows={3}
                style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              />
              <div style={{ marginTop: '12px', textAlign: 'right' }}>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handlePost}
                  loading={postMutation.isPending}
                  disabled={!newComment.trim()}
                  style={{ background: '#d4af37', border: 'none' }}
                >
                  Post Comment
                </Button>
              </div>
            </div>
          </div>
        </Card>
      ) : (
        <Card
          style={{
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '8px',
            marginBottom: '24px',
            textAlign: 'center',
          }}
          bodyStyle={{ padding: '24px' }}
        >
          <Text style={{ color: '#a0a0a0' }}>
            Sign in to join the conversation
          </Text>
          <br />
          <Button
            type="primary"
            onClick={handleLogin}
            style={{ marginTop: '12px', background: '#d4af37', border: 'none' }}
          >
            Sign in with Google
          </Button>
        </Card>
      )}

      {/* Comments list */}
      {isLoading && (
        <div style={{ textAlign: 'center', padding: '24px' }}>
          <Spin />
        </div>
      )}

      {comments && comments.length === 0 && (
        <Text type="secondary">No comments yet. Be the first to share your thoughts!</Text>
      )}

      {comments && comments.map((comment) => (
        <Comment key={comment.id} comment={comment} essaySlug={essaySlug} />
      ))}
    </section>
  );
}

/**
 * Table of contents component.
 */
function TableOfContents({ toc }: { toc: Array<{ id: string; text: string; level: number }> }) {
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -80% 0px' }
    );

    toc.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [toc]);

  if (toc.length === 0) return null;

  return (
    <nav
      style={{
        position: 'sticky',
        top: '100px',
        padding: '16px',
        background: 'rgba(255, 255, 255, 0.03)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '8px',
      }}
    >
      <Text strong style={{ color: '#f5f5f5', display: 'block', marginBottom: '12px' }}>
        Contents
      </Text>
      {toc.map(({ id, text, level }) => (
        <a
          key={id}
          href={`#${id}`}
          style={{
            display: 'block',
            padding: '4px 0',
            paddingLeft: `${(level - 1) * 12}px`,
            color: activeId === id ? '#d4af37' : '#a0a0a0',
            textDecoration: 'none',
            fontSize: '0.85rem',
            transition: 'color 0.2s',
          }}
        >
          {text}
        </a>
      ))}
    </nav>
  );
}

/**
 * Public essay page component.
 */
function PublicEssayPage() {
  const { slug } = useParams<{ slug: string }>();
  const contentRef = useRef<HTMLDivElement>(null);
  const essayContentRef = useRef<HTMLDivElement>(null);
  const [readingProgress, setReadingProgress] = useState(0);

  // Audio playback state
  const [audioSegments, setAudioSegments] = useState<AudioSegment[]>([]);
  const [audioVisible, setAudioVisible] = useState(false);
  const [currentSegmentId, setCurrentSegmentId] = useState<string | null>(null);
  // Viewport-relative Y for the floating player card (tracks active paragraph)
  const [playerY, setPlayerY] = useState(180);

  // Fetch essay
  const {
    data: essay,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['public', 'essay', slug],
    queryFn: () => getPublicEssay(slug!),
    enabled: !!slug,
  });

  // Reading progress
  useEffect(() => {
    function handleScroll() {
      if (!contentRef.current) return;
      const { top, height } = contentRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const scrolled = Math.max(0, -top);
      const total = height - windowHeight;
      const progress = Math.min(100, Math.max(0, (scrolled / total) * 100));
      setReadingProgress(progress);
    }

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch audio manifest when the essay has audio
  useEffect(() => {
    if (!essay?.hasAudio || !slug) return;
    fetch(`/api/public/essays/${slug}/audio/manifest.json`)
      .then(r => r.json())
      .then((data: AudioSegment[]) => setAudioSegments(data))
      .catch(() => {});
  }, [essay?.hasAudio, slug]);

  // Inject data-segment-id attributes into rendered essay block elements.
  // Replicates the backend's segmentation counters so IDs match the manifest:
  //   headings → h-0, h-1, ...  (independent counter)
  //   paragraphs + blockquotes → p-0, p-1, ...  (shared counter)
  //   pre / hr / ul / ol / etc. → skipped (no ID)
  useEffect(() => {
    const container = essayContentRef.current;
    if (!container || !audioSegments.length) return;

    let pCount = 0;
    let hCount = 0;

    for (const el of Array.from(container.children)) {
      const tag = el.tagName.toLowerCase();
      if (/^h[1-6]$/.test(tag)) {
        el.setAttribute('data-segment-id', `h-${hCount++}`);
      } else if (tag === 'p') {
        el.setAttribute('data-segment-id', `p-${pCount++}`);
      } else if (tag === 'blockquote') {
        el.setAttribute('data-segment-id', `p-${pCount++}`);
      }
      // pre, hr, ul, ol, table, etc. — not voiced, no ID
    }
  }, [audioSegments, essay?.content]);

  // Highlight the active segment, scroll it into view, and track its Y for the player.
  useEffect(() => {
    // Clear previous highlight
    essayContentRef.current?.querySelectorAll('[data-segment-id].audio-active').forEach(el => {
      el.classList.remove('audio-active');
    });

    if (!currentSegmentId) return;

    const el = essayContentRef.current?.querySelector(`[data-segment-id="${currentSegmentId}"]`);
    if (el) {
      el.classList.add('audio-active');
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // After smooth scroll the element will be near center of viewport,
      // so anchor the floating player there too.
      const rect = el.getBoundingClientRect();
      // Use current rect as a best-effort position; transition handles the lag.
      setPlayerY(Math.max(80, rect.top));
    }
  }, [currentSegmentId]);

  // Loading state
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert
        message="Essay not found"
        description="The essay you're looking for doesn't exist or hasn't been published yet."
        type="error"
        showIcon
      />
    );
  }

  if (!essay) return null;

  // Render markdown content
  const htmlContent = marked(essay.content) as string;

  return (
    <div>
      {/* Reading progress bar */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: 'rgba(255, 255, 255, 0.1)',
          zIndex: 99,
        }}
      >
        <div
          style={{
            width: `${readingProgress}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #d4af37, #f5d742)',
            transition: 'width 0.1s',
          }}
        />
      </div>

      <div style={{ display: 'flex', gap: '48px' }}>
        {/* Main content */}
        <article style={{ flex: 1, minWidth: 0, maxWidth: '800px', margin: '0 auto' }} ref={contentRef}>
          {/* Category and tags */}
          <div style={{ marginBottom: '16px' }}>
            {essay.category && (
              <Link to={`/category/${essay.category.slug}`}>
                <Tag
                  style={{
                    background: 'rgba(212, 175, 55, 0.15)',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    color: '#d4af37',
                  }}
                >
                  {essay.category.name}
                </Tag>
              </Link>
            )}
          </div>

          {/* Title */}
          <Title
            style={{
              fontSize: '2.8rem',
              fontFamily: "var(--font-serif)",
              fontWeight: 400,
              color: '#f5f5f5',
              marginBottom: '16px',
              lineHeight: 1.2,
              textAlign: 'center',
              textShadow: '0 0 60px rgba(212, 175, 55, 0.18), 0 0 120px rgba(212, 175, 55, 0.08)',
            }}
          >
            {essay.title}
          </Title>

          {/* Meta info */}
          <div style={{ marginBottom: '32px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
            <span style={{ color: '#888' }}>
              <ClockCircleOutlined style={{ marginRight: '4px' }} />
              {essay.readingTime} min read
            </span>
            {/* Listen button hidden — will re-enable once essays are voice-recorded */}
            {/* {essay.hasAudio && audioSegments.length > 0 && !audioVisible && (
              <Button
                icon={<SoundOutlined />}
                size="small"
                onClick={() => setAudioVisible(true)}
                style={{
                  background: 'rgba(212, 175, 55, 0.1)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  color: '#d4af37',
                  borderRadius: '20px',
                  fontSize: '0.8rem',
                }}
              >
                Listen
              </Button>
            )} */}
          </div>

          {/* Essay content */}
          <div
            ref={essayContentRef}
            className="essay-content"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
            style={{
              color: '#d0d0d0',
              fontSize: '1.1rem',
              lineHeight: 1.8,
              textAlign: 'left',
            }}
          />

          {/* Tags */}
          {essay.tags && essay.tags.length > 0 && (
            <div style={{ marginTop: '32px' }}>
              <Divider style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
              <Space wrap>
                {essay.tags.map((tag) => (
                  <Link key={tag.id} to={`/tag/${tag.slug}`}>
                    <Tag
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        color: '#a0a0a0',
                      }}
                    >
                      #{tag.name}
                    </Tag>
                  </Link>
                ))}
              </Space>
            </div>
          )}

          {/* Palate Cleanser — track companion (Spotify/YouTube) */}
          {essay.trackEmbed && (
            <div style={{ marginTop: '64px' }}>
              <Divider style={{ borderColor: 'rgba(255, 255, 255, 0.06)' }} />
              <div style={{ padding: '8px 0 0' }}>
                {/youtube(?:-nocookie)?\.com\/embed\//.test(essay.trackEmbed) ? (
                  <YouTubePlayer embedHtml={essay.trackEmbed} />
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: essay.trackEmbed }} />
                )}
              </div>
              {essay.lyrics && (
                <div style={{
                  marginTop: '24px',
                  padding: '24px',
                  background: 'rgba(255, 255, 255, 0.02)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                }}>
                  <Text style={{
                    color: 'rgba(255, 255, 255, 0.3)',
                    fontSize: '0.7rem',
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase' as const,
                    display: 'block',
                    marginBottom: '16px',
                  }}>
                    Lyrics
                  </Text>
                  <div
                    style={{
                      color: 'rgba(255, 255, 255, 0.55)',
                      fontFamily: 'var(--font-serif)',
                      fontSize: '0.95rem',
                      lineHeight: '1.8',
                      whiteSpace: 'pre-wrap',
                    }}
                    dangerouslySetInnerHTML={{ __html: marked.parse(essay.lyrics) as string }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Series bottom navigation */}
          {essay.seriesInfo && essay.seriesInfo.length > 0 && essay.seriesInfo[0] && (() => {
            const s = essay.seriesInfo[0];
            return (
              <div style={{ marginTop: '64px' }}>
                <Divider style={{ borderColor: 'rgba(255, 255, 255, 0.06)' }} />
                <div style={{ textAlign: 'center', padding: '16px 0 8px' }}>
                  <Text style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                    {s.series.name} &nbsp;·&nbsp; {s.orderIndex + 1} of {s.totalInSeries}
                  </Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'stretch', gap: '16px', marginTop: '24px' }}>
                  {s.previousEssay ? (
                    <Link to={`/essays/${s.previousEssay.slug}`} style={{ flex: 1, textDecoration: 'none' }}>
                      <div style={{
                        padding: '20px 24px',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s',
                        height: '100%',
                      }}
                        onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(212,175,55,0.4)')}
                        onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)')}
                      >
                        <Text style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                          <ArrowLeftOutlined style={{ marginRight: '6px' }} />Previous
                        </Text>
                        <Text style={{ color: '#f5f5f5', fontFamily: "var(--font-serif)", fontSize: '1.1rem' }}>
                          {s.previousEssay.title}
                        </Text>
                      </div>
                    </Link>
                  ) : <div style={{ flex: 1 }} />}

                  {s.nextEssay ? (
                    <Link to={`/essays/${s.nextEssay.slug}`} style={{ flex: 1, textDecoration: 'none' }}>
                      <div style={{
                        padding: '20px 24px',
                        border: '1px solid rgba(212,175,55,0.25)',
                        borderRadius: '8px',
                        textAlign: 'right',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s, background 0.2s',
                        height: '100%',
                      }}
                        onMouseEnter={e => {
                          e.currentTarget.style.borderColor = 'rgba(212,175,55,0.6)';
                          e.currentTarget.style.background = 'rgba(212,175,55,0.04)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.borderColor = 'rgba(212,175,55,0.25)';
                          e.currentTarget.style.background = 'transparent';
                        }}
                      >
                        <Text style={{ color: '#d4af37', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                          Next <ArrowRightOutlined style={{ marginLeft: '6px' }} />
                        </Text>
                        <Text style={{ color: '#f5f5f5', fontFamily: "var(--font-serif)", fontSize: '1.1rem' }}>
                          {s.nextEssay.title}
                        </Text>
                      </div>
                    </Link>
                  ) : (
                    <div style={{
                      flex: 1,
                      padding: '20px 24px',
                      border: '1px solid rgba(255,255,255,0.06)',
                      borderRadius: '8px',
                      textAlign: 'right',
                    }}>
                      <Text style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>
                        End of series
                      </Text>
                      <Text style={{ color: 'rgba(255,255,255,0.3)', fontFamily: "var(--font-serif)", fontSize: '1.1rem' }}>
                        {s.series.name}
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Comments section */}
          <CommentsSection essaySlug={slug!} />
        </article>

        {/* Sidebar with TOC */}
        {essay.tableOfContents && essay.tableOfContents.length > 0 && (
          <aside style={{ width: '250px', flexShrink: 0 }}>
            <TableOfContents toc={essay.tableOfContents} />
          </aside>
        )}
      </div>

      {/* Floating audio player card — rendered when user clicks Listen */}
      {audioVisible && audioSegments.length > 0 && (
        <AudioPlayer
          slug={slug!}
          segments={audioSegments}
          targetY={playerY}
          onSegmentChange={setCurrentSegmentId}
          onClose={() => {
            setAudioVisible(false);
            setCurrentSegmentId(null);
          }}
        />
      )}

      {/* Styles for essay content */}
      <style>{`
        .essay-content h1,
        .essay-content h2,
        .essay-content h3,
        .essay-content h4 {
          color: #f5f5f5;
          font-family: var(--font-serif);
          margin-top: 2em;
          margin-bottom: 0.5em;
        }
        .essay-content h1 { font-size: 2rem; }
        .essay-content h1:first-child { display: none; }
        .essay-content h2 { font-size: 1.6rem; }
        .essay-content h3 { font-size: 1.3rem; }
        .essay-content p { margin-bottom: 1.5em; }
        .essay-content a { color: #d4af37; }
        .essay-content a:hover { text-decoration: underline; }
        .essay-content blockquote {
          border-left: 3px solid rgba(212, 175, 55, 0.7);
          box-shadow: -4px 0 20px rgba(212, 175, 55, 0.12);
          padding-left: 1.2em;
          margin-left: 0;
          color: #a0a0a0;
          font-style: italic;
          background: rgba(212, 175, 55, 0.03);
          border-radius: 0 6px 6px 0;
          padding-top: 0.4em;
          padding-bottom: 0.4em;
        }
        .essay-content pre {
          background: rgba(0, 0, 0, 0.3);
          border-radius: 8px;
          padding: 1em;
          overflow-x: auto;
        }
        .essay-content code {
          background: rgba(0, 0, 0, 0.3);
          padding: 0.2em 0.4em;
          border-radius: 4px;
          font-size: 0.9em;
        }
        .essay-content pre code {
          background: none;
          padding: 0;
        }
        .essay-content img {
          max-width: 100%;
          border-radius: 8px;
        }
        .essay-content ul,
        .essay-content ol {
          padding-left: 1.5em;
        }
        .essay-content li {
          margin-bottom: 0.5em;
        }

        /* Dancing bloom keyframes for active audio segment */
        @keyframes audiobloom {
          0%, 100% {
            box-shadow:
              0 0 0px rgba(212, 175, 55, 0),
              0 0 12px rgba(212, 175, 55, 0.18),
              inset 0 0 0px rgba(212, 175, 55, 0);
          }
          50% {
            box-shadow:
              0 0 0 2px rgba(212, 175, 55, 0.12),
              0 0 32px rgba(212, 175, 55, 0.38),
              inset 0 0 24px rgba(212, 175, 55, 0.04);
          }
        }

        .essay-content [data-segment-id] {
          transition: background 0.35s ease, padding 0.35s ease, margin 0.35s ease;
          border-radius: 6px;
        }
        .essay-content [data-segment-id].audio-active {
          background: linear-gradient(
            135deg,
            rgba(212, 175, 55, 0.10) 0%,
            rgba(212, 175, 55, 0.04) 60%,
            rgba(212, 175, 55, 0.07) 100%
          );
          padding-left: 0.8em;
          padding-right: 0.5em;
          margin-left: -0.8em;
          margin-right: -0.5em;
          animation: audiobloom 2.4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

export default PublicEssayPage;
